import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Grid,
  TextField,
  Button,
  Divider,
  useTheme,
  Card,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Badge,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Switch,
  FormControlLabel,
  Snackbar,
  InputAdornment,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
  Category as CategoryIcon,
  VerifiedUser as VerifiedIcon,
  SupervisorAccount as SupervisorIcon,
  AdminPanelSettings as AdminIcon,
  CameraAlt as CameraIcon,
  Notifications as NotificationIcon,
  Palette as PaletteIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  BarChart as ChartIcon,
  Key as KeyIcon,
  VisibilityOff as VisibilityOffIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import axios from "axios";
import { backend_url } from "../App";
import { getCurrentUser, getAuthToken } from "../utils/AuthHelper";

export default function Profile() {
  const theme = useTheme();
  const [user, setUser] = useState(getCurrentUser());
  const token = getAuthToken();
  const [isEditing, setIsEditing] = useState(false);
  const [editedUser, setEditedUser] = useState({ ...user });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  // Data states
  const [organizationDetails, setOrganizationDetails] = useState(null);
  const [createdByUser, setCreatedByUser] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [events, setEvents] = useState([]);
  const [organizationSpecies, setOrganizationSpecies] = useState([]);
  const [userSpecies, setUserSpecies] = useState([]);
  const [userStats, setUserStats] = useState(null);

  const [passwordData, setPasswordData] = useState({
    old_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [showPassword, setShowPassword] = useState({
    old: false,
    new: false,
    confirm: false,
  });

  // Wrap fetchProfileData in useCallback
  const fetchProfileData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Token ${token}` };

      // 1. Get current user from accounts/users/me endpoint
      const userRes = await axios.get(`${backend_url}/accounts/api/users/me/`, {
        headers,
      });
      const updatedUser = userRes.data;
      console.log("User data:", updatedUser);
      setUser(updatedUser);
      setEditedUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));

      // 2. Get organization details if user has organization
      if (updatedUser.organization) {
        const orgId =
          typeof updatedUser.organization === "object"
            ? updatedUser.organization.id
            : updatedUser.organization;

        try {
          const orgRes = await axios.get(
            `${backend_url}/accounts/api/organizations/${orgId}/`,
            { headers },
          );
          setOrganizationDetails(orgRes.data);
        } catch (orgErr) {
          console.log("Error fetching organization:", orgErr);
          if (typeof updatedUser.organization === "object") {
            setOrganizationDetails(updatedUser.organization);
          }
        }
      }

      // 3. Get created_by user details
      if (updatedUser.created_by) {
        const creatorId =
          typeof updatedUser.created_by === "object"
            ? updatedUser.created_by.id
            : updatedUser.created_by;

        try {
          const creatorRes = await axios.get(
            `${backend_url}/accounts/api/users/${creatorId}/`,
            { headers },
          );
          setCreatedByUser(creatorRes.data);
        } catch (creatorErr) {
          console.log("Error fetching creator:", creatorErr);
          if (typeof updatedUser.created_by === "object") {
            setCreatedByUser(updatedUser.created_by);
          }
        }
      }

      // 4. Get user species permissions
      try {
        const userSpeciesRes = await axios.get(
          `${backend_url}/core/api/user-species-permissions/?user=${updatedUser.id}`,
          { headers },
        );
        const permissions =
          userSpeciesRes.data.results || userSpeciesRes.data || [];
        setUserSpecies(permissions);
      } catch (err) {
        console.log("Error fetching user species permissions:", err);
      }

      // 5. Get organization species permissions (for org admins)
      if (updatedUser.user_type === "superuser" && updatedUser.organization) {
        const orgId =
          typeof updatedUser.organization === "object"
            ? updatedUser.organization.id
            : updatedUser.organization;

        try {
          const orgSpeciesRes = await axios.get(
            `${backend_url}/core/api/organization-species-permissions/?organization=${orgId}`,
            { headers },
          );
          setOrganizationSpecies(
            orgSpeciesRes.data.results || orgSpeciesRes.data || [],
          );
        } catch (err) {
          console.log("Error fetching organization species permissions:", err);
        }
      }

      // 6. Get cameras for the user
      try {
        const camerasRes = await axios.get(`${backend_url}/core/api/camera/`, {
          headers,
        });
        const allCameras = camerasRes.data.results || camerasRes.data || [];

        // Filter cameras based on user's organization
        let userCameras = allCameras;
        if (updatedUser.user_type === "superuser") {
          // Org admin sees cameras in their organization
          const orgId =
            typeof updatedUser.organization === "object"
              ? updatedUser.organization.id
              : updatedUser.organization;
          userCameras = allCameras.filter(
            (c) => c.organization === orgId || c.organization?.id === orgId,
          );
        } else if (
          updatedUser.user_type === "user" ||
          updatedUser.user_type === "viewer"
        ) {
          // Regular users see cameras they have permission for
          // This will be further filtered by events with their species
          userCameras = allCameras;
        }
        setCameras(userCameras);
      } catch (err) {
        console.log("Error fetching cameras:", err);
      }

      // 7. Get events for the user (filtered by their species)
      try {
        let speciesIds = [];

        // Get species IDs from user permissions
        if (updatedUser.user_type === "admin") {
          // Admin sees all events, no need to filter by species
          const eventsRes = await axios.get(`${backend_url}/core/api/event/`, {
            headers,
            params: { page_size: 100 },
          });
          setEvents(eventsRes.data.results || eventsRes.data || []);
        } else {
          // For non-admin users, get species from permissions
          const speciesFromUserPerms = userSpecies.map((p) =>
            typeof p.specie === "object" ? p.specie.id : p.specie,
          );

          // For org admins, also include organization species
          let orgSpeciesIds = [];
          if (updatedUser.user_type === "superuser") {
            orgSpeciesIds = organizationSpecies.map((p) =>
              typeof p.specie === "object" ? p.specie.id : p.specie,
            );
          }

          speciesIds = [
            ...new Set([...speciesFromUserPerms, ...orgSpeciesIds]),
          ];

          if (speciesIds.length > 0) {
            const eventsRes = await axios.get(
              `${backend_url}/core/api/event/`,
              {
                headers,
                params: {
                  species: speciesIds.join(","),
                  page_size: 100,
                },
              },
            );
            setEvents(eventsRes.data.results || eventsRes.data || []);
          }
        }
      } catch (err) {
        console.log("Error fetching events:", err);
      }

      // 8. Get user stats if admin/superuser
      if (
        updatedUser.user_type === "admin" ||
        updatedUser.user_type === "superuser"
      ) {
        try {
          const statsRes = await axios.get(
            `${backend_url}/accounts/api/users/stats/`,
            { headers },
          );
          setUserStats(statsRes.data);
        } catch (err) {
          console.log("Error fetching stats:", err);
        }
      }

      setLoading(false);
    } catch (err) {
      console.error("Error fetching profile data:", err);
      setError("Failed to load profile data");
      setLoading(false);
    }
  }, [token, organizationSpecies, userSpecies]);

  useEffect(() => {
    fetchProfileData();
  }, [fetchProfileData]); // Add fetchProfileData as dependency

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      const headers = { Authorization: `Token ${token}` };

      const payload = {
        first_name: editedUser.first_name || "",
        last_name: editedUser.last_name || "",
      };

      // Only admin can change email
      if (user.user_type === "admin" && editedUser.email !== user.email) {
        payload.email = editedUser.email;
      }

      await axios.patch(
        `${backend_url}/accounts/api/users/${user.id}/`,
        payload,
        { headers },
      );

      // Refresh user data
      const userRes = await axios.get(`${backend_url}/accounts/api/users/me/`, {
        headers,
      });
      setUser(userRes.data);
      setEditedUser(userRes.data);
      localStorage.setItem("user", JSON.stringify(userRes.data));

      setSuccess("Profile updated successfully");
      setIsEditing(false);
    } catch (err) {
      console.error("Error updating profile:", err);
      setError(err.response?.data?.detail || "Failed to update profile");
    }
  };

  const handleCancel = () => {
    setEditedUser({ ...user });
    setIsEditing(false);
  };

  const handleChange = (field, value) => {
    setEditedUser((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleChangePassword = async () => {
    try {
      if (passwordData.new_password !== passwordData.confirm_password) {
        setError("New passwords do not match");
        return;
      }

      if (passwordData.new_password.length < 8) {
        setError("Password must be at least 8 characters long");
        return;
      }

      // Using the change_password endpoint from accounts
      const headers = { Authorization: `Token ${token}` };
      await axios.post(
        `${backend_url}/accounts/api/users/${user.id}/change_password/`,
        passwordData,
        { headers },
      );

      setSuccess("Password changed successfully");
      setPasswordData({
        old_password: "",
        new_password: "",
        confirm_password: "",
      });
    } catch (err) {
      console.error("Error changing password:", err);
      setError(err.response?.data?.detail || "Failed to change password");
    }
  };

  const toggleShowPassword = (field) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const getOrganizationName = () => {
    if (!user.organization) return "No Organization";
    if (typeof user.organization === "object") {
      return user.organization.name || "Unknown Organization";
    }
    return organizationDetails?.name || "Loading...";
  };

  const getUserTypeIcon = (type) => {
    switch (type) {
      case "admin":
        return <AdminIcon />;
      case "superuser":
        return <SupervisorIcon />;
      case "user":
        return <PersonIcon />;
      case "viewer":
        return <VisibilityIcon />;
      default:
        return <PersonIcon />;
    }
  };

  const getUserTypeColor = (type) => {
    switch (type) {
      case "admin":
        return "error";
      case "superuser":
        return "warning";
      case "user":
        return "success";
      case "viewer":
        return "info";
      default:
        return "default";
    }
  };

  const getHierarchyInfo = () => {
    if (
      (user.user_type === "user" ||
        user.user_type === "viewer" ||
        user.user_type === "superuser") &&
      createdByUser
    ) {
      return {
        title: "Created by",
        name: createdByUser.username,
        email: createdByUser.email,
        type: createdByUser.user_type_display || createdByUser.user_type,
        createdDate: new Date(user.date_created).toLocaleDateString(),
      };
    }
    return null;
  };

  // Get species with full details from permissions
  const getSpeciesFromPermissions = () => {
    if (user.user_type === "admin") {
      // Admin doesn't need to see species list here
      return [];
    }

    if (user.user_type === "superuser") {
      // Org admin sees organization species
      return organizationSpecies.map((perm) => ({
        id: typeof perm.specie === "object" ? perm.specie.id : perm.specie,
        name:
          perm.specie_name ||
          (typeof perm.specie === "object" ? perm.specie.name : perm.specie),
        color: perm.specie?.color || "#1976d2",
        endangered: perm.specie?.endangered || false,
        protected: perm.specie?.protected || false,
        enabled: perm.specie?.enabled !== false,
        granted_by: perm.granted_by_username,
        granted_at: perm.granted_at,
      }));
    }

    // Regular user sees their personal species permissions
    return userSpecies.map((perm) => ({
      id: typeof perm.specie === "object" ? perm.specie.id : perm.specie,
      name:
        perm.specie_name ||
        (typeof perm.specie === "object" ? perm.specie.name : perm.specie),
      color: perm.specie?.color || "#1976d2",
      endangered: perm.specie?.endangered || false,
      protected: perm.specie?.protected || false,
      enabled: perm.specie?.enabled !== false,
      granted_by: perm.granted_by_username,
      granted_at: perm.granted_at,
    }));
  };

  // Get cameras with events for user's species
  const getCamerasWithUserEvents = () => {
    if (!cameras.length || !events.length) return [];

    // Get unique camera IDs from events
    const cameraIdsWithEvents = [
      ...new Set(
        events
          .map((event) => event.camera?.id)
          .filter((id) => id !== undefined && id !== null),
      ),
    ];

    // Filter cameras that have events
    return cameras.filter((camera) => cameraIdsWithEvents.includes(camera.id));
  };

  const renderPersonalInfo = () => (
    <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Personal Information
        </Typography>
        {!isEditing ? (
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEdit}
            size="small"
          >
            Edit
          </Button>
        ) : (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              size="small"
            >
              Save
            </Button>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
              size="small"
            >
              Cancel
            </Button>
          </Box>
        )}
      </Box>
      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            label="First Name"
            value={
              isEditing ? editedUser.first_name || "" : user.first_name || ""
            }
            onChange={(e) => handleChange("first_name", e.target.value)}
            fullWidth
            disabled={!isEditing}
            variant={isEditing ? "outlined" : "standard"}
            placeholder={user.first_name ? "" : "First Name"}
            InputLabelProps={{ shrink: true }}
            InputProps={
              !isEditing ? { readOnly: true, disableUnderline: true } : {}
            }
            sx={
              !isEditing
                ? {
                    "& .MuiInputBase-root": {
                      "&::before": { borderBottom: "none" },
                      "&::after": { borderBottom: "none" },
                    },
                  }
                : {}
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Last Name"
            value={
              isEditing ? editedUser.last_name || "" : user.last_name || ""
            }
            onChange={(e) => handleChange("last_name", e.target.value)}
            fullWidth
            disabled={!isEditing}
            variant={isEditing ? "outlined" : "standard"}
            placeholder={user.last_name ? "" : "Last Name"}
            InputLabelProps={{ shrink: true }}
            InputProps={
              !isEditing ? { readOnly: true, disableUnderline: true } : {}
            }
            sx={
              !isEditing
                ? {
                    "& .MuiInputBase-root": {
                      "&::before": { borderBottom: "none" },
                      "&::after": { borderBottom: "none" },
                    },
                  }
                : {}
            }
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Username"
            value={user.username || ""}
            fullWidth
            disabled
            variant="standard"
            InputLabelProps={{ shrink: true }}
            InputProps={{
              readOnly: true,
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiInputBase-root": {
                "&::before": { borderBottom: "none" },
                "&::after": { borderBottom: "none" },
              },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Email"
            value={isEditing ? editedUser.email || "" : user.email || ""}
            onChange={(e) => handleChange("email", e.target.value)}
            fullWidth
            disabled={!isEditing || user.user_type !== "admin"}
            variant={
              isEditing && user.user_type === "admin" ? "outlined" : "standard"
            }
            InputLabelProps={{ shrink: true }}
            InputProps={{
              readOnly: user.user_type !== "admin",
              disableUnderline: user.user_type !== "admin" || !isEditing,
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
            sx={
              user.user_type !== "admin" || !isEditing
                ? {
                    "& .MuiInputBase-root": {
                      "&::before": { borderBottom: "none" },
                      "&::after": { borderBottom: "none" },
                    },
                  }
                : {}
            }
          />
          {user.user_type !== "admin" && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              Contact administrator to change email
            </Typography>
          )}
        </Grid>
      </Grid>
    </Paper>
  );

  const renderAccountInfo = () => (
    <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Account Information
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <List>
        <ListItem>
          <ListItemIcon>
            <CalendarIcon color="action" />
          </ListItemIcon>
          <ListItemText
            primary="Member Since"
            secondary={new Date(
              user.date_created || Date.now(),
            ).toLocaleDateString()}
          />
        </ListItem>
        <ListItem>
          <ListItemIcon>
            <CalendarIcon color="action" />
          </ListItemIcon>
          <ListItemText
            primary="Last Active"
            secondary={
              user.last_active
                ? new Date(user.last_active).toLocaleString()
                : "Never"
            }
          />
        </ListItem>
        <ListItem>
          <ListItemIcon>
            <SecurityIcon color="action" />
          </ListItemIcon>
          <ListItemText
            primary="Account Status"
            secondary={
              <Chip
                icon={user.is_active ? <CheckCircleIcon /> : <WarningIcon />}
                label={user.is_active ? "Active" : "Inactive"}
                color={user.is_active ? "success" : "error"}
                size="small"
              />
            }
          />
        </ListItem>
        <ListItem>
          <ListItemIcon>
            <VerifiedIcon color="action" />
          </ListItemIcon>
          <ListItemText
            primary="Verification Status"
            secondary={
              <Chip
                label={user.is_verified ? "Verified" : "Not Verified"}
                color={user.is_verified ? "success" : "default"}
                size="small"
              />
            }
          />
        </ListItem>
      </List>
    </Paper>
  );

  const renderOrganizationInfo = () => (
    <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Organization Information
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {organizationDetails ? (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
            <BusinessIcon sx={{ fontSize: 40, color: "primary.main" }} />
            <Box>
              <Typography variant="h5" fontWeight={600}>
                {organizationDetails.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {organizationDetails.description || "No description available"}
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Users
                </Typography>
                <Typography variant="h4" color="primary">
                  {organizationDetails.user_count || 0}
                </Typography>
                <Typography variant="caption">
                  of {organizationDetails.max_users} maximum
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Cameras
                </Typography>
                <Typography variant="h4" color="secondary">
                  {organizationDetails.cameras_count || 0}
                </Typography>
                <Typography variant="caption">
                  Active cameras in organization
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}
              >
                <Typography variant="body2" color="text.secondary">
                  Joined On
                </Typography>
                <Typography variant="body2">
                  {new Date(organizationDetails.joined_on).toLocaleDateString()}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </>
      ) : user.organization ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <BusinessIcon sx={{ fontSize: 40, color: "primary.main" }} />
          <Box>
            <Typography variant="h5" fontWeight={600}>
              {typeof user.organization === "object"
                ? user.organization.name
                : "Organization"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Details loading...
            </Typography>
          </Box>
        </Box>
      ) : (
        <Alert severity="info">
          You are not associated with any organization.
        </Alert>
      )}
    </Paper>
  );

  const renderHierarchyInfo = () => {
    const hierarchy = getHierarchyInfo();
    if (!hierarchy) return null;

    return (
      <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Hierarchical Information
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 2 }}>
          <Avatar sx={{ bgcolor: "primary.main" }}>{hierarchy.name[0]}</Avatar>
          <Box>
            <Typography variant="h6">{hierarchy.title}</Typography>
            <Typography variant="body1">{hierarchy.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {hierarchy.email}
            </Typography>
          </Box>
          <Chip
            icon={getUserTypeIcon(createdByUser?.user_type)}
            label={hierarchy.type}
            color={getUserTypeColor(createdByUser?.user_type)}
            sx={{ ml: "auto" }}
          />
        </Box>

        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="body2" color="text.secondary">
            Account Created On
          </Typography>
          <Typography variant="body2">{hierarchy.createdDate}</Typography>
        </Box>
      </Paper>
    );
  };

  const renderPermittedSpecies = () => {
    const speciesList = getSpeciesFromPermissions();

    return (
      <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            {user.user_type === "superuser"
              ? "Organization Species"
              : "My Species"}
          </Typography>
          <Chip
            label={`${speciesList.length} Species`}
            color="info"
            size="small"
          />
        </Box>
        <Divider sx={{ mb: 3 }} />

        {speciesList.length > 0 ? (
          <Grid container spacing={2}>
            {speciesList.map((specie) => (
              <Grid item xs={12} sm={6} md={4} key={specie.id}>
                <Card
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderColor: specie.color + "40",
                    backgroundColor: specie.color + "10",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        backgroundColor: specie.color,
                      }}
                    />
                    <Typography variant="subtitle2" fontWeight={600}>
                      {specie.name}
                    </Typography>
                    {specie.endangered && (
                      <Chip
                        label="Endangered"
                        color="error"
                        size="small"
                        sx={{ ml: "auto" }}
                      />
                    )}
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    ID: {specie.id}
                  </Typography>
                  {specie.granted_by && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Granted by: {specie.granted_by}
                    </Typography>
                  )}
                  <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                    <Chip
                      label={specie.enabled ? "Enabled" : "Disabled"}
                      size="small"
                      color={specie.enabled ? "success" : "default"}
                      variant="outlined"
                    />
                    {specie.protected && (
                      <Chip
                        label="Protected"
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Alert severity="info">
            {user.user_type === "superuser"
              ? "No species assigned to your organization. Contact system administrator."
              : "No species permissions assigned. Contact your organization administrator."}
          </Alert>
        )}
      </Paper>
    );
  };

  const renderCamerasInfo = () => {
    const camerasWithEvents = getCamerasWithUserEvents();

    return (
      <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Cameras with Events
          </Typography>
          <Chip
            label={`${camerasWithEvents.length} Cameras`}
            color="info"
            size="small"
          />
        </Box>
        <Divider sx={{ mb: 3 }} />

        {camerasWithEvents.length > 0 ? (
          <Grid container spacing={2}>
            {camerasWithEvents.slice(0, 6).map((camera) => (
              <Grid item xs={12} sm={6} md={4} key={camera.id}>
                <Card variant="outlined" sx={{ p: 2, height: "100%" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 1,
                    }}
                  >
                    <CameraIcon color="action" />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {camera.description || `Camera ${camera.id}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {camera.id}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}
                  >
                    <Chip
                      label={camera.live ? "Live" : "Offline"}
                      size="small"
                      color={camera.live ? "success" : "error"}
                    />
                    <Chip
                      label={camera.should_log ? "Logging" : "No Logs"}
                      size="small"
                      color={camera.should_log ? "info" : "default"}
                    />
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Last Reported:{" "}
                      {camera.last_reported_at
                        ? new Date(camera.last_reported_at).toLocaleDateString()
                        : "Never"}
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Events:{" "}
                      {events.filter((e) => e.camera?.id === camera.id).length}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Alert severity="info">
            {events.length === 0
              ? "No events found for your species. Events will appear when cameras capture your assigned species."
              : "No cameras found with events for your species."}
          </Alert>
        )}
      </Paper>
    );
  };

  const renderStatistics = () => (
    <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Statistics & Analytics
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {userStats ? (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined" sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="h4" color="primary">
                {userStats.total_users}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Users
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined" sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="h4" color="success.main">
                {userStats.active_users}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Users
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined" sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="h4" color="warning.main">
                {userStats.superuser_count}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Organization Admins
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined" sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="h4" color="info.main">
                {userStats.user_count}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Regular Users
              </Typography>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <Alert severity="info">
          Statistics are only available for administrators.
        </Alert>
      )}
    </Paper>
  );

  const renderSecurityTab = () => (
    <Paper sx={{ p: 3, borderRadius: 3 }}>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Security & Preferences
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Change Password
          </Typography>
          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Current Password"
                  type={showPassword.old ? "text" : "password"}
                  value={passwordData.old_password}
                  onChange={(e) =>
                    handlePasswordChange("old_password", e.target.value)
                  }
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => toggleShowPassword("old")}
                          edge="end"
                        >
                          {showPassword.old ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="New Password"
                  type={showPassword.new ? "text" : "password"}
                  value={passwordData.new_password}
                  onChange={(e) =>
                    handlePasswordChange("new_password", e.target.value)
                  }
                  fullWidth
                  helperText="Minimum 8 characters"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => toggleShowPassword("new")}
                          edge="end"
                        >
                          {showPassword.new ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Confirm New Password"
                  type={showPassword.confirm ? "text" : "password"}
                  value={passwordData.confirm_password}
                  onChange={(e) =>
                    handlePasswordChange("confirm_password", e.target.value)
                  }
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => toggleShowPassword("confirm")}
                          edge="end"
                        >
                          {showPassword.confirm ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<KeyIcon />}
                    onClick={handleChangePassword}
                    disabled={
                      !passwordData.old_password ||
                      !passwordData.new_password ||
                      !passwordData.confirm_password
                    }
                  >
                    Change Password
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={() =>
                      setPasswordData({
                        old_password: "",
                        new_password: "",
                        confirm_password: "",
                      })
                    }
                  >
                    Clear
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <NotificationIcon sx={{ mr: 2, color: "primary.main" }} />
              <Typography variant="subtitle2">Notifications</Typography>
            </Box>
            <FormControlLabel
              control={<Switch defaultChecked />}
              label="Email Alerts"
            />
            <FormControlLabel
              control={<Switch defaultChecked />}
              label="Push Notifications"
            />
            <FormControlLabel control={<Switch />} label="SMS Alerts" />
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <PaletteIcon sx={{ mr: 2, color: "primary.main" }} />
              <Typography variant="subtitle2">Appearance</Typography>
            </Box>
            <FormControlLabel control={<Switch />} label="Dark Mode" />
            <FormControlLabel
              control={<Switch defaultChecked />}
              label="Compact View"
            />
          </Card>
        </Grid>
      </Grid>
    </Paper>
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: { xs: 2, md: 3 } }}>
      {/* Header Section */}
      <Paper sx={{ p: 4, mb: 4, borderRadius: 3, position: "relative" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 3,
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 3, md: 0 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              badgeContent={
                <Avatar sx={{ width: 24, height: 24, bgcolor: "primary.main" }}>
                  {getUserTypeIcon(user.user_type)}
                </Avatar>
              }
            >
              <Avatar
                sx={{
                  width: 120,
                  height: 120,
                  fontSize: "3rem",
                  bgcolor: theme.palette.primary.main,
                }}
              >
                {user.first_name?.[0]?.toUpperCase() ||
                  user.username?.[0]?.toUpperCase() ||
                  "U"}
              </Avatar>
            </Badge>
            <Box>
              <Typography variant="h4" fontWeight={600} gutterBottom>
                {`${user.first_name || ""} ${user.last_name || ""}`.trim() ||
                  user.username}
              </Typography>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {user.email}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Chip
                  icon={getUserTypeIcon(user.user_type)}
                  label={user.user_type_display || user.user_type}
                  color={getUserTypeColor(user.user_type)}
                  variant="outlined"
                />
                {user.organization && (
                  <Chip
                    icon={<BusinessIcon />}
                    label={getOrganizationName()}
                    color="secondary"
                    variant="outlined"
                  />
                )}
                <Chip
                  icon={user.is_active ? <LockOpenIcon /> : <LockIcon />}
                  label={user.is_active ? "Active" : "Inactive"}
                  color={user.is_active ? "success" : "error"}
                  variant="outlined"
                />
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="Refresh Profile">
              <IconButton onClick={fetchProfileData} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleEdit}
              sx={{ display: { xs: "none", md: "flex" } }}
            >
              Edit Profile
            </Button>
            <IconButton
              color="primary"
              onClick={handleEdit}
              sx={{ display: { xs: "flex", md: "none" } }}
            >
              <EditIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab icon={<PersonIcon />} label="Personal" />
          <Tab icon={<BusinessIcon />} label="Organization" />
          <Tab icon={<CategoryIcon />} label="Species" />
          <Tab icon={<CameraIcon />} label="Cameras" />
          {user.user_type === "admin" || user.user_type === "superuser" ? (
            <Tab icon={<ChartIcon />} label="Statistics" />
          ) : null}
          <Tab icon={<SecurityIcon />} label="Security" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box sx={{ mt: 2 }}>
        {activeTab === 0 && (
          <>
            {renderPersonalInfo()}
            {renderAccountInfo()}
            {renderHierarchyInfo()}
          </>
        )}

        {activeTab === 1 && (
          <>
            {renderOrganizationInfo()}
            {(user.user_type === "user" || user.user_type === "viewer") &&
              renderHierarchyInfo()}
          </>
        )}

        {activeTab === 2 && renderPermittedSpecies()}

        {activeTab === 3 && renderCamerasInfo()}

        {activeTab === 4 &&
          (user.user_type === "admin" || user.user_type === "superuser") && (
            <>
              {renderStatistics()}
              {user.user_type === "admin" && (
                <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    System Overview
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  <Stepper orientation="vertical">
                    <Step active>
                      <StepLabel>System Administration</StepLabel>
                      <StepContent>
                        <Typography>
                          You have full access to all organizations, users, and
                          cameras.
                        </Typography>
                      </StepContent>
                    </Step>
                    <Step active>
                      <StepLabel>User Management</StepLabel>
                      <StepContent>
                        <Typography>
                          Can create and manage all user types across
                          organizations.
                        </Typography>
                      </StepContent>
                    </Step>
                    <Step active>
                      <StepLabel>Species Permissions</StepLabel>
                      <StepContent>
                        <Typography>
                          Can assign species permissions to organization
                          administrators.
                        </Typography>
                      </StepContent>
                    </Step>
                  </Stepper>
                </Paper>
              )}
            </>
          )}

        {activeTab ===
          (user.user_type === "admin" || user.user_type === "superuser"
            ? 5
            : 4) && renderSecurityTab()}
      </Box>

      {/* Footer */}
      <Paper sx={{ p: 3, borderRadius: 3, mt: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              User ID: {user.id}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Last Updated:{" "}
              {new Date(user.date_modified || Date.now()).toLocaleString()}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            sx={{ textAlign: { xs: "left", md: "right" } }}
          >
            <Typography variant="caption" color="text.secondary">
              © {new Date().getFullYear()} Camera Trap Monitoring System
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Snackbars */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
}
